import { T } from '../Vendors/VendorDetails';

export type RemediationStatus = 'in_progress' | 'awaiting_review' | 'completed' | 'archived';
export type RiskSeverity = 'Critical' | 'High' | 'Medium';

export interface RemediationSummary {
  id: string;
  reference: string;
  title: string;
  sender_org_name?: string | null;
  vendor_id: string;
  vendor_name: string | null;
  vendor_domain: string | null;
  status: RemediationStatus;
  due_date: string | null;
  overdue: boolean;
  reminder_days: number | null;
  risk_count: number;
  open_risk_count: number;
  fixed_risk_count: number;
  open_by_severity: Record<RiskSeverity, number>;
  categories: string[];
  recipients: string[];
  initial_score: number | null;
  projected_score: number | null;
  current_score: number | null;
  vendor_score: number | null;
  created_by_name: string | null;
  last_reminder_at: string | null;
  message: string | null;
  created_at: string | null;
  updated_at: string | null;
  submitted_at: string | null;
  completed_at: string | null;
  archived_at: string | null;
  last_message_at: string | null;
  last_message_by: 'tenant' | 'vendor' | null;
}

export interface RemediationRisk {
  id: string;
  name: string;
  category: string | null;
  severity: RiskSeverity | null;
  current_severity: string | null;
  status: 'open' | 'fixed';
  score_impact: number | null;
  description: string | null;
  recommendation: string | null;
  evidence: string | null;
  fixed_at: string | null;
}

export interface RemediationMessage {
  id: string;
  author_type: 'tenant' | 'vendor';
  author_name: string | null;
  author_email: string | null;
  body: string;
  created_at: string;
}

export interface RemediationEvent { id: string; kind: string; actor: string | null; note: string | null; created_at: string }

export interface RemediationRecipient { id: string; email: string; name: string | null; invited_at: string | null; last_seen_at: string | null }

export interface RemediationDetailPayload {
  request: RemediationSummary;
  risks: RemediationRisk[];
  recipients: RemediationRecipient[];
  messages: RemediationMessage[];
  events: RemediationEvent[];
}

export const STATUS_META: Record<RemediationStatus, { label: string; light: string; dark: string }> = {
  in_progress: { label: 'In progress', light: '#2563EB', dark: '#60A5FA' },
  awaiting_review: { label: 'Awaiting review', light: '#B45309', dark: '#FBBF24' },
  completed: { label: 'Completed', light: '#15803D', dark: '#4ADE80' },
  archived: { label: 'Archived', light: '#64748B', dark: '#94A3B8' },
};

export const SEVERITY_COLOR: Record<RiskSeverity, { light: string; dark: string }> = {
  Critical: { light: '#DC2626', dark: '#f87171' },
  High: { light: '#EA580C', dark: '#fb923c' },
  Medium: { light: '#D97706', dark: '#fbbf24' },
};
export const SEVERITY_RANK: Record<string, number> = { Critical: 0, High: 1, Medium: 2 };

export const palette = (isDark: boolean) => ({
  ink: isDark ? '#e2e8f0' : T.ink,
  slate: isDark ? '#94a3b8' : T.slate,
  muted: isDark ? '#94a3b8' : T.muted,
  line: isDark ? '#1f1f1f' : T.bdr,
  rowLine: isDark ? '#161616' : '#F1F5F9',
  surface: isDark ? '#0d0d0d' : T.surface,
  surface2: isDark ? '#141414' : T.surface2,
  bg: isDark ? '#000' : T.bg,
  accent: isDark ? '#60A5FA' : T.blue,
  danger: isDark ? '#F87171' : T.red,
  amber: isDark ? '#FBBF24' : T.amb,
  green: isDark ? '#4ADE80' : T.grn,
  selected: isDark ? '#0f1e38' : T.blueBg,
});

export const apiError = (error: any, fallback: string) => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d?.msg).filter(Boolean).join('. ') || fallback;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') return detail.message;
  return fallback;
};

export const fmtDate = (value?: string | null) =>
  value ? new Date(value.length === 10 ? `${value}T00:00:00` : value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
export const fmtDateTime = (value?: string | null) =>
  value ? new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
export const daysUntil = (iso: string) => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - now.getTime()) / 86400000);
};
export const relative = (value?: string | null) => {
  if (!value) return '—';
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return fmtDate(value);
};

export function StatusPill({ status, isDark }: { status: RemediationStatus; isDark: boolean }) {
  const meta = STATUS_META[status] || STATUS_META.in_progress;
  const color = isDark ? meta.dark : meta.light;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
      color, background: `${color}${isDark ? '22' : '12'}`, border: `1px solid ${color}${isDark ? '55' : '33'}`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />{meta.label}
    </span>
  );
}

export function SeverityDot({ severity, isDark }: { severity: string | null; isDark: boolean }) {
  const meta = severity && SEVERITY_COLOR[severity as RiskSeverity];
  const color = meta ? (isDark ? meta.dark : meta.light) : (isDark ? '#94a3b8' : T.slate);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 11.5, fontWeight: 600, color, whiteSpace: 'nowrap' }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />{severity || 'Not flagged'}
    </span>
  );
}

/** Risk-score colour, same bands as the platform (Low >= 80, Medium >= 60, High >= 40, else Critical). */
export const scoreColor = (score: number | null | undefined, isDark: boolean) => {
  if (score === null || score === undefined) return isDark ? '#94a3b8' : T.slate;
  if (score >= 80) return isDark ? '#4ADE80' : T.grn;
  if (score >= 60) return isDark ? '#FBBF24' : T.amb;
  if (score >= 40) return isDark ? '#fb923c' : '#EA580C';
  return isDark ? '#f87171' : T.red;
};

export const EVENT_LABEL: Record<string, string> = {
  created: 'Request created',
  sent: 'Sent to',
  submitted: 'Submitted for review',
  completed: 'Marked completed',
  reopened: 'Sent back to the vendor',
  archived: 'Archived',
  unarchived: 'Unarchived',
  updated: 'Updated',
  risk_fixed: 'Fixed according to the latest scan',
  risk_reopened: 'Detected again by the latest scan',
  all_fixed: 'Every risk fixed according to the latest scan',
  reminder_sent: 'Due-date reminder emailed',
  recipient_added: 'Contact added',
  recipient_removed: 'Contact removed',
  invite_resent: 'Invitation re-sent to',
};

export const csvCell = (value: unknown) => {
  let text = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@]/.test(text)) text = `'${text}`;
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};
export const downloadCsv = (filename: string, header: string[], rows: unknown[][]) => {
  const csv = [header, ...rows].map(r => r.map(csvCell).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

/** Requested -> Submitted for review -> Completed. A request sent back to the vendor is at step 1 again ("Sent back"). */
export function RequestStepper({ r, isDark, compact = false }: { r: RemediationSummary; isDark: boolean; compact?: boolean }) {
  const c = palette(isDark);
  const stage = r.status === 'completed' ? 2 : r.status === 'awaiting_review' ? 1 : r.status === 'archived'
    ? (r.completed_at ? 2 : r.submitted_at ? 1 : 0) : 0;
  const sentBack = r.status === 'in_progress' && !!r.submitted_at;
  const steps = [
    { label: 'Requested', date: r.created_at },
    { label: sentBack ? 'Sent back' : 'Submitted', date: r.submitted_at },
    { label: 'Completed', date: r.completed_at },
  ];
  const done = isDark ? '#e2e8f0' : T.ink;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
      {steps.map((s, i) => {
        const reached = i <= stage;
        return (
          <div key={s.label} style={{ display: 'flex', alignItems: 'flex-start', flex: i < steps.length - 1 ? 1 : '0 0 auto' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: i === 0 ? 'flex-start' : i === steps.length - 1 ? 'flex-end' : 'center', minWidth: 0 }}>
              <span style={{ width: compact ? 10 : 12, height: compact ? 10 : 12, borderRadius: '50%', marginTop: 2, flexShrink: 0,
                background: reached ? done : 'transparent', border: `2px solid ${reached ? done : c.line}`,
                boxShadow: i === stage && r.status !== 'archived' ? `0 0 0 3px ${isDark ? '#ffffff22' : '#0F172A14'}` : 'none' }} />
              <span style={{ fontSize: compact ? 10.5 : 11.5, fontWeight: reached ? 600 : 500, color: reached ? c.ink : c.muted, marginTop: 6, whiteSpace: 'nowrap',
                ...(sentBack && i === 1 ? { color: c.amber } : {}) }}>{s.label}</span>
              {!compact && <span style={{ fontSize: 10.5, color: c.muted, marginTop: 1, whiteSpace: 'nowrap' }}>{s.date ? fmtDate(s.date) : ' '}</span>}
            </div>
            {i < steps.length - 1 && <span style={{ flex: 1, height: 2, marginTop: compact ? 6 : 7, marginLeft: 6, marginRight: 6, background: i < stage ? done : c.line, minWidth: 16 }} />}
          </div>
        );
      })}
    </div>
  );
}
